/**
 * API Reference:
 * - https://developer.mozilla.org/en-US/docs/Web/API/Fullscreen_API
 *
 * Reference Implementations:
 * - https://github.com/sindresorhus/screenfull
 * - https://github.com/rafgraph/fscreen
 */

type FullscreenAPI = Readonly<{
  fullscreenEnabled: string;
  fullscreenElement: string;
  requestFullscreen: string;
  exitFullscreen: string;
  eventPrefix: string;
}>;

const standard: FullscreenAPI = {
  fullscreenEnabled: 'fullscreenEnabled',
  fullscreenElement: 'fullscreenElement',
  requestFullscreen: 'requestFullscreen',
  exitFullscreen: 'exitFullscreen',
  eventPrefix: 'fullscreen',
};

const webkit: FullscreenAPI = {
  fullscreenElement: 'webkitFullscreenElement',
  fullscreenEnabled: 'webkitFullscreenEnabled',
  requestFullscreen: 'webkitRequestFullscreen',
  exitFullscreen: 'webkitExitFullscreen',
  eventPrefix: 'webkitfullscreen',
};

const document =
  typeof window !== 'undefined' ? window.document : ({} as unknown as Document);

const api =
  standard.fullscreenEnabled in document
    ? standard
    : webkit.fullscreenEnabled in document
    ? webkit
    : ({} as unknown as FullscreenAPI);

/**
 * Fullscreen API wrapper that provides a cross-browser consistent API.
 *
 * _Supports all major browsers released since December 2018,
 * plus Chromium-based and Safari browsers released since 2012._
 *
 */
export const fullscreen = {
  get isEnabled(): boolean {
    return Boolean(document[api.fullscreenEnabled as keyof Document]);
  },

  get isFullscreen(): boolean {
    return Boolean(this.element);
  },

  get element(): Element | null {
    return document[api.fullscreenElement as keyof Document] as Element | null;
  },

  async request(
    element = document.documentElement,
    options?: FullscreenOptions,
  ): Promise<void> {
    type Func = Element['requestFullscreen'];
    return (element[api.requestFullscreen as keyof Element] as Func)(options);
  },

  async exit(): Promise<void> {
    type Func = Document['exitFullscreen'];
    return (document[api.exitFullscreen as keyof Document] as Func)();
  },

  async toggle(
    element = document.documentElement,
    options?: FullscreenOptions,
  ): Promise<void> {
    return this.isFullscreen ? this.exit() : this.request(element, options);
  },

  on(
    event: 'change' | 'error',
    listener: () => void,
    options?: AddEventListenerOptions,
  ): void {
    document.addEventListener(api.eventPrefix + event, listener, options);
  },

  off(
    event: 'change' | 'error',
    listener: () => void,
    options?: AddEventListenerOptions,
  ): void {
    document.removeEventListener(api.eventPrefix + event, listener, options);
  },
};
